import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import PlatformLayout from "../../components/platform/PlatformLayout";
import { PlatformZone } from "../../components/platform/PlatformZone";
import { PlatformZones } from "../../components/platform/PlatformZones";
import { 
 FaCheckCircle,
 FaPaperPlane
} from "react-icons/fa";
import { api } from "../../services/api";

const Contact: React.FC = () => {
 const { t } = useTranslation();
 const googleReCaptcha = useGoogleReCaptcha();
 const [formData, setFormData] = useState({
  name: "",
  email: "",
  subject: "",
  message: "",
 });
 const [errors, setErrors] = useState<Record<string, string>>({});
 const [loading, setLoading] = useState(false);
 const [success, setSuccess] = useState(false);

 useEffect(() => {
  const observerOptions = {
   threshold: 0.1,
   rootMargin: "0px 0px -100px 0px",
  };

  const observer = new IntersectionObserver((entries) => {
   entries.forEach((entry) => {
    if (entry.isIntersecting) {
     entry.target.classList.add("animate-fade-in");
    }
   });
  }, observerOptions);

  const elements = document.querySelectorAll(".scroll-animate");
  elements.forEach((el) => observer.observe(el));

  return () => {
   elements.forEach((el) => observer.unobserve(el));
  };
 }, []);

 const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
   ...prev,
   [name]: value,
  }));
  if (errors[name]) {
   setErrors((prev) => {
    const newErrors = { ...prev };
    delete newErrors[name];
    return newErrors;
   });
  }
 };

 const validateForm = (): boolean => {
  const newErrors: Record<string, string> = {};

  if (!formData.name.trim()) {
   newErrors.name = t("platform.contact.errors.nameRequired") || "Name is required";
  }

  if (!formData.email.trim()) {
   newErrors.email = t("platform.contact.errors.emailRequired") || "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
   newErrors.email = t("platform.contact.errors.emailInvalid") || "Please enter a valid email address";
  }

  if (!formData.subject.trim()) {
   newErrors.subject = t("platform.contact.errors.subjectRequired") || "Subject is required";
  }

  if (!formData.message.trim()) {
   newErrors.message = t("platform.contact.errors.messageRequired") || "Message is required";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) {
   return;
  }

  setLoading(true);
  setErrors({});

  try {
   // In test mode (Cypress), bypass reCAPTCHA
   const isTestMode = typeof window !== "undefined" && (window as any).Cypress;
   let captchaValue = "";
   
   if (isTestMode) {
    captchaValue = "test-token-cypress";
   } else {
    // Check if reCAPTCHA is available
    if (!googleReCaptcha) {
     setErrors({ form: t("platform.contact.errors.captchaNotReady") || "CAPTCHA is not ready. Please refresh the page and try again." });
     setLoading(false);
     return;
    }

    try {
     captchaValue = await (googleReCaptcha as any)?.executeV3("contact");
     if (!captchaValue || captchaValue.trim() === "") {
      setErrors({ form: t("platform.contact.errors.captchaNotReady") || "CAPTCHA verification failed. Please refresh the page and try again." });
      setLoading(false);
      return;
     }
    } catch (captchaError: any) {
     console.error("reCAPTCHA execution error:", captchaError);
     setErrors({ form: t("platform.contact.errors.captchaNotReady") || "CAPTCHA verification failed. Please refresh the page and try again." });
     setLoading(false);
     return;
    }
   }
   
   // Submit contact form
   await api.post("/public/contact", {
    name: formData.name.trim(),
    email: formData.email.trim(),
    subject: formData.subject.trim(),
    message: formData.message.trim(),
    captchaValue,
   });
   
   setSuccess(true);
   setFormData({ name: "", email: "", subject: "", message: "" });
  } catch (error: any) {
   const errorMessage = error.response?.data?.error || 
    t("platform.contact.errors.submitFailed") || 
    "Failed to send message. Please try again.";
   setErrors({ form: errorMessage });
  } finally {
   setLoading(false);
  }
 };

 return (
  <PlatformLayout>
   {/* Hero Section - Editable Zone */}
   <PlatformZone pageSlug="contact" zoneName="hero" />

   {/* Contact Section */}
   <section className="py-20 px-4">
    <div className="max-w-6xl mx-auto">
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      {/* Contact Info and Business Hours - Dynamically render zones (excluding hero) */}
      <div>
       <PlatformZones pageSlug="contact" excludeZones={["hero"]} />
      </div>

      {/* Contact Form - Original functional form (not in a zone) */}
      <div className="bg-white  p-8 scroll-animate opacity-0">
       {success ? (
        <div className="text-center py-8">
         <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
         <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {t("platform.contact.success.title") || "Message Sent!"}
         </h3>
         <p className="text-gray-600">
          {t("platform.contact.success.message") || "Thank you for contacting us. We'll get back to you soon."}
         </p>
        </div>
       ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
         <div>
          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
           {t("platform.contact.form.name") || "Name"} <span className="text-red-500">*</span>
          </label>
          <input
           type="text"
           id="name"
           name="name"
           value={formData.name}
           onChange={handleChange}
           className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.name ? "border-red-500" : "border-gray-300"
           }`}
           required
          />
          {errors.name && (
           <p className="mt-1 text-sm text-red-500">{errors.name}</p>
          )}
         </div>

         <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
           {t("platform.contact.form.email") || "Email"} <span className="text-red-500">*</span>
          </label>
          <input
           type="email"
           id="email"
           name="email"
           value={formData.email}
           onChange={handleChange}
           className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.email ? "border-red-500" : "border-gray-300"
           }`}
           required
          />
          {errors.email && (
           <p className="mt-1 text-sm text-red-500">{errors.email}</p>
          )}
         </div>

         <div>
          <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
           {t("platform.contact.form.subject") || "Subject"} <span className="text-red-500">*</span>
          </label>
          <input
           type="text"
           id="subject"
           name="subject"
           value={formData.subject}
           onChange={handleChange}
           className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.subject ? "border-red-500" : "border-gray-300"
           }`}
           required
          />
          {errors.subject && (
           <p className="mt-1 text-sm text-red-500">{errors.subject}</p>
          )}
         </div>

         <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
           {t("platform.contact.form.message") || "Message"} <span className="text-red-500">*</span>
          </label>
          <textarea
           id="message"
           name="message"
           value={formData.message}
           onChange={handleChange}
           rows={6}
           className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.message ? "border-red-500" : "border-gray-300"
           }`}
           required
          />
          {errors.message && (
           <p className="mt-1 text-sm text-red-500">{errors.message}</p>
          )}
         </div>

         {errors.form && (
          <div className="bg-red-50 border border-red-200 p-4">
           <p className="text-sm text-red-600">{errors.form}</p>
          </div>
         )}

         <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-4 font-bold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
         >
          {loading ? (
           <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {t("platform.contact.form.sending") || "Sending..."}
           </>
          ) : (
           <>
            {t("platform.contact.form.send") || "Send Message"}
            <FaPaperPlane className="ml-2" />
           </>
          )}
         </button>

         <p className="text-xs text-center text-gray-500">
          {t("platform.contact.form.captchaNote") || "This form is protected by reCAPTCHA"}
         </p>
        </form>
       )}
      </div>
     </div>
    </div>
   </section>

   <style>{`
    @keyframes fade-in {
     from { opacity: 0; transform: translateY(20px); }
     to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in {
     animation: fade-in 0.6s ease-out forwards;
    }
   `}</style>
  </PlatformLayout>
 );
};

export default Contact;

